// LiveSessions.tsx
import { useMemo } from 'react'
import {
  Checkbox,
  HStack,
  SimpleGrid,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react'
import { groupBy, keys, map, values } from 'lodash'
import moment, { Moment } from 'moment'
import { deleteField } from 'firebase/firestore'
import useStore, { MonthlyReport, Reports } from '../../../../store/useStore'
import { getDaysOfWeekForMonth } from '../utils/constants'
import { CoreProgramsDisplay } from './CoreProgramsDisplay'

const LiveSessions = ({
  reports,
  month,
  update,
  coreProgramsCounts = { live: 0, recording: 0 },
}: {
  reports: Reports
  month?: string
  update?: (partial: Partial<MonthlyReport>) => Promise<void>
  coreProgramsCounts?: Record<string, number>
}) => {
  const omitSundays = useStore((state) => state.meta?.omitSundays || [])
  const report = (!!update && !!month && reports[month]) || undefined

  const days = useMemo(() => {
    const moments: Moment[] = []

    const months = month ? [month] : keys(reports)

    months.forEach((reportingFor) => {
      const m = moment(new Date(reportingFor + '-15'))
      const yearMonthNumber = parseInt(reportingFor.replace('-', ''))
      const daysOfWeek = getDaysOfWeekForMonth(yearMonthNumber)

      for (let i = -30; i < 30; i++) {
        const m2 = m.clone().add(i, 'days')

        if (m2.month() === m.month() && daysOfWeek.includes(m2.weekday())) {
          moments.push(m2)
        }
      }
    })

    return groupBy(moments, (m) => m.weekday())
  }, [month, reports])

  const noneChecked =
    report?.sundays &&
    !values(report.sundays).some((val) => val === 'live' || val === 'recording')

  return (
    <Stack
      borderColor={update ? 'gray.200' : undefined}
      borderRadius={update ? 'xl' : undefined}
      borderWidth={update ? '1px' : 0}
      p={update ? { base: 4, md: 6 } : 0}
      spacing={5}
    >
      {!!update && (
        <Text fontSize='xl' fontWeight='bold'>
          Which Healing ALS sessions did you attend this month? *
        </Text>
      )}
      {!!update && (
        <Text color='gray.600'>
          Select every session you attended live or watched as a recording. Your
          program hours update automatically.
        </Text>
      )}
      {!!update && <CoreProgramsDisplay coreProgramsCounts={coreProgramsCounts} />}
      {!!update && !!month && (
        <HStack spacing={2}>
          <Checkbox
            aria-label='I did not attend or watch any sessions this month'
            isChecked={noneChecked}
            onChange={(e) => {
              if (noneChecked) {
                update({
                  sundays: deleteField() as any,
                })
              } else {
                update({
                  sundays: {},
                })
              }
            }}
          />
          <Text>I did not attend or watch any sessions this month</Text>
        </HStack>
      )}

      <Stack spacing={4}>
        {map(days, (days, dayOfWeek) => {
          return (
            <SimpleGrid key={dayOfWeek} columns={[1, 2, 5]} spacing={4}>
              {days.map((moment) => {
                const date = moment.format('YYYY-MM-DD')
                const omitted = omitSundays.includes(date)

                const report = reports[moment.format('YYYY-MM')]
                const status = report?.sundays?.[date]

                return (
                  <Stack
                    borderColor='gray.200'
                    borderRadius='xl'
                    borderWidth='1px'
                    p={4}
                    key={date}
                    spacing={1}
                    backgroundColor={omitted ? 'gray.100' : 'gray.50'}
                  >
                    <Text fontSize={{ base: 'md', md: 'sm' }}>
                      <Tag
                        size='sm'
                        backgroundColor={
                          moment.weekday() === 1
                            ? 'green.200'
                            : moment.weekday() === 4
                            ? 'blue.200'
                            : 'purple.200'
                        }
                      >
                        {moment.format('ddd')}
                      </Tag>{' '}
                      {moment.format('l')}
                    </Text>
                    {omitted ? (
                      <Text fontSize={{ base: 'md', md: 'sm' }}>No Meeting</Text>
                    ) : (
                      <Stack spacing={0}>
                        <HStack spacing={2}>
                          <Checkbox
                            aria-label={`${moment.format('LL')} attended live`}
                            size='sm'
                            isDisabled={omitted || !update}
                            isChecked={status === 'live'}
                            onChange={(e) => {
                              update?.({
                                sundays: {
                                  ...report?.sundays,
                                  [date]: e.target.checked ? 'live' : 'none',
                                },
                              })
                            }}
                          />
                          <Text fontSize={{ base: 'md', md: 'xs' }}>Attended Live</Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Checkbox
                            aria-label={`${moment.format('LL')} watched recording`}
                            size='sm'
                            isDisabled={omitted || !update}
                            isChecked={status === 'recording'}
                            onChange={(e) => {
                              update?.({
                                sundays: {
                                  ...report?.sundays,
                                  [date]: e.target.checked
                                    ? 'recording'
                                    : 'none',
                                },
                              })
                            }}
                          />
                          <Text fontSize={{ base: 'md', md: 'xs' }}>
                            Watched Recording
                          </Text>
                        </HStack>
                      </Stack>
                    )}
                  </Stack>
                )
              })}
            </SimpleGrid>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default LiveSessions
